/**
 * Shared sEEG / other channel classification (#764).
 *
 * Decides which channels may enter the Common Average / Common Median reference.
 * Mirror of `common/channel_kind.py`; both consume the fixture
 * `channel_kind_cases.json`. Keep the two in step.
 *
 * Rule, in order:
 * 1. Header first — an EDF+ `<TYPE> <name>` prefix MNE recognises: EEG/SEEG/ECOG/DBS → `seeg`,
 *    any other known type → `other`; otherwise a known physical dimension that is not a voltage
 *    unit → `other`.
 * 2. Label heuristic — strip a leading `EEG `; no digit → `other`; a stem (text before the first
 *    digit) in {@link NON_SEEG_STEMS} → `other`; else `seeg`.
 *
 * Bipolar pairing helpers (#1088), label-only: {@link isExcludedChannel} (a stem in
 * {@link EXCLUDED_STEMS}: never a pair, never an electrode group) and
 * {@link signedPairBase} (`EKG+` / `EKG-` → `EKG`: one `+`-minus-`-` pair).
 * {@link isNonElectrodeLabel} is the label-only non-electrode test Bipolar passthrough uses
 * (#1088, Product review of PR #1116).
 *
 * Numbered series (#1088, code review of PR #1116): a label alone cannot tell oximetry
 * `SpO2` from contact 2 of an electrode `SPO`. {@link electrodeSeriesBases} reads the whole
 * recording; a label whose base has at least two numbered contacts (`SPO1`, `SPO2`) is
 * judged on that base only. The predicates take it as the optional `series` argument and,
 * without it, judge the label alone. {@link classifyChannels} and the Bipolar and group
 * builders pass it.
 */

export type ChannelKind = 'seeg' | 'other';

/** Per-channel signal-header fields as stored in `meta.ch_header` by the upload. */
export interface ChannelHeader {
    type_prefix: string | null;
    physical_dimension: string;
    transducer: string;
}

export const EDF_TYPE_PREFIXES: ReadonlySet<string> = new Set([
    'EEG', 'SEEG', 'ECOG', 'DBS', 'EOG', 'ECG', 'EMG', 'BIO', 'RESP', 'TEMP', 'MISC', 'SAO2', 'STIM',
]);
export const EDF_SEEG_TYPE_PREFIXES: ReadonlySet<string> = new Set(['EEG', 'SEEG', 'ECOG', 'DBS']);
export const VOLTAGE_UNITS: ReadonlySet<string> = new Set(['uv', 'µv', 'μv', 'mv', 'v']);
export const NON_SEEG_STEMS: ReadonlySet<string> = new Set([
    'ECG', 'EKG', 'EMG', 'EOG', 'RESP', 'SPO2', 'SAO2', 'PLETH', 'DC', 'MKR', 'TRIG', 'PULSE',
    'PHOTIC', 'EVENT', 'ANNOT', 'STIM', 'TEMP', 'BIO', 'MISC', 'CO', 'CO2', 'ETCO', 'ETCO2',
]);

/**
 * Stems of channels that are never wanted as electrode contacts (#1088): they form no
 * bipolar pair and no electrode group, are listed in "Other", shown as-is under Bipolar
 * and unselected by default in Create Analysis. Matched whole, after the normalisation
 * of {@link isExcludedChannel}. One stem per line; mirror of `EXCLUDED_STEMS` in
 * `common/channel_kind.py`.
 */
export const EXCLUDED_STEMS: ReadonlySet<string> = new Set([
    'MKR',
    'DC',
    'SAO2',
    'SPO2',
    'ETCO2',
]);

const EEG_PREFIX_RE = /^EEG\s+/i;
// Nihon Kohden labels every channel "POL <name>" regardless of kind (see `channelKindForLabel`).
const POL_PREFIX_RE = /^POL\s+/i;
/** A numbered contact (`SPO12`, `SPO 3`): a base that does not end in a space, then its number. */
const SERIES_CONTACT_RE = /^(\D*[^\d\s])\s*(\d+)$/;
/** A Bipolar row of two contacts (`SPO1-SPO2`); both bases must match. */
const SERIES_ROW_RE = /^(\D*[^\d\s])\s*(\d+)\s*-\s*(\D*[^\d\s])\s*\d+$/;

/** Numbered series of a recording: the bases of {@link electrodeSeriesBases}. */
export type ChannelSeries = ReadonlySet<string>;

const stripPrefixes = (label: string): string =>
    (label ?? '').replace(EEG_PREFIX_RE, '').replace(POL_PREFIX_RE, '').trim();

/**
 * `[base, number]` of a numbered contact or of a Bipolar row of one electrode, else `null`.
 * `EEG ` / `POL ` and spaces are ignored and the base is upper-cased: `POL SPO 3` →
 * `["SPO", 3]`, `SPO1-SPO2` → `["SPO", 1]`. A signed label (`MKR1+`) is no member.
 */
function seriesMember(label: string): [string, number] | null {
    const cleaned = stripPrefixes(label);
    let match = SERIES_CONTACT_RE.exec(cleaned);
    if (!match) {
        match = SERIES_ROW_RE.exec(cleaned);
        if (!match || stripPrefixes(match[3]!).toUpperCase() !== match[1]!.toUpperCase()) return null;
    }
    const base = match[1]!;
    if (!/[A-Za-z]/.test(base)) return null;
    return [base.trim().toUpperCase(), parseInt(match[2]!, 10)];
}

/**
 * Bases carried by at least two of `labels` with different numbers (#1088).
 * `SPO1`, `SPO2` → {"SPO"}; `SpO2` alone, or `SpO2` twice, → {}. Mirror of
 * `electrode_series_bases` in `common/channel_kind.py`.
 */
export function electrodeSeriesBases(labels: Iterable<string>): Set<string> {
    const numbers = new Map<string, Set<number>>();
    for (const label of labels) {
        const member = seriesMember(label);
        if (!member) continue;
        if (!numbers.has(member[0])) numbers.set(member[0], new Set());
        numbers.get(member[0])!.add(member[1]);
    }
    return new Set([...numbers].filter(([, found]) => found.size >= 2).map(([base]) => base));
}

/** The base of `label` when it is a member of one of the `series`, else `null`. */
function seriesBase(label: string, series?: ChannelSeries): string | null {
    if (!series || series.size === 0) return null;
    const member = seriesMember(label);
    return member && series.has(member[0]) ? member[0] : null;
}

/**
 * Candidate stems of a label: the text before each of its digits plus the whole label,
 * upper-cased. `SpO21` → {"SPO", "SPO2", "SPO21"}. Empty when there is no digit.
 * A member of one of the `series` has its base as only stem (`SPO2` next to `SPO1`).
 */
export function labelStems(label: string, series?: ChannelSeries): Set<string> {
    const cleaned = (label ?? '').replace(EEG_PREFIX_RE, '').trim();
    if (cleaned.search(/\d/) < 0) return new Set();
    const base = seriesBase(label, series);
    if (base !== null) return new Set([base]);
    const stems = new Set<string>();
    for (let i = 0; i < cleaned.length; i++) {
        if (/\d/.test(cleaned[i]!)) stems.add(cleaned.slice(0, i).trim().toUpperCase());
    }
    stems.add(cleaned.toUpperCase());
    return stems;
}

/** Label-only verdict: the historical "Other group" rule plus the stem list. */
export function heuristicKind(label: string, series?: ChannelSeries): ChannelKind {
    const stems = labelStems(label, series);
    if (stems.size === 0) return 'other'; // no digit at all
    for (const s of stems) {
        if (NON_SEEG_STEMS.has(s)) return 'other';
    }
    return 'seeg';
}

/** Verdict from the file's own information, or `null` when it carries none. */
export function headerKind(header: ChannelHeader | null | undefined): ChannelKind | null {
    if (!header) return null;
    const prefix = header.type_prefix?.toUpperCase();
    if (prefix && EDF_TYPE_PREFIXES.has(prefix)) {
        return EDF_SEEG_TYPE_PREFIXES.has(prefix) ? 'seeg' : 'other';
    }
    const unit = (header.physical_dimension ?? '').trim().toLowerCase();
    if (unit && !VOLTAGE_UNITS.has(unit)) return 'other';
    return null;
}

/** Final kind of one channel: header verdict when present, else the heuristic. */
export function classifyChannel(label: string, header?: ChannelHeader | null, series?: ChannelSeries): ChannelKind {
    return headerKind(header) ?? heuristicKind(label, series);
}

/**
 * Kinds aligned with `labels`; `headers` (same length) is optional. Numbered series are
 * read from `labels` themselves ({@link electrodeSeriesBases}).
 */
export function classifyChannels(labels: readonly string[], headers?: readonly (ChannelHeader | null)[] | null): ChannelKind[] {
    const series = electrodeSeriesBases(labels);
    if (!headers || headers.length !== labels.length) {
        return labels.map(l => classifyChannel(l, null, series));
    }
    return labels.map((l, i) => classifyChannel(l, headers[i], series));
}

/**
 * Kind of the channel named `label`, preferring the kinds the file itself declares
 * (`channelKinds`, aligned with `channelNames`, computed server-side from the EDF signal
 * header) and falling back to the label heuristic when they are missing, misaligned, or
 * the label is not an EDF channel (PR #1012 review, round 2, point 2).
 */
// Nihon Kohden labels every channel "POL <name>" regardless of kind, hiding
// "EMG"/"EOG" from the heuristic's own stem check (PR #1012 review, round 3,
// point 1.2). Stripped only in this fallback -- `heuristicKind`/`classifyChannel`
// stay unchanged for any other caller -- same scoping as `app/models/edf.py`'s
// identical fix on the backend.

export function channelKindForLabel(
    label: string,
    channelNames: readonly string[],
    channelKinds?: readonly ChannelKind[] | null,
): ChannelKind {
    if (channelKinds && channelKinds.length === channelNames.length) {
        const index = channelNames.indexOf(label);
        if (index >= 0) return channelKinds[index]!;
    }
    return heuristicKind(label.replace(POL_PREFIX_RE, ''), electrodeSeriesBases(channelNames ?? []));
}

/** Stems of `word`; a digit-less word is its own stem, with and without a trailing sign. */
function wordStems(word: string): Set<string> {
    const stems = new Set<string>();
    for (const form of new Set([word, word.replace(/[+-]+$/, '')])) {
        const formStems = labelStems(form);
        if (formStems.size === 0 && form) formStems.add(form.toUpperCase());
        formStems.forEach(s => stems.add(s));
    }
    return stems;
}

/**
 * Stems of `label` without its `EEG `/`POL ` prefix, plus those of its first and last
 * words. They cover EDF+ type-style labels from real exports, with the type first
 * (`SaO2 PULS+`) or a type that is not a known prefix (`Value MKR1+`, `ECG ECG+`).
 */
function nameStems(label: string): Set<string> {
    const cleaned = (label ?? '').replace(EEG_PREFIX_RE, '').replace(POL_PREFIX_RE, '').trim();
    const stems = wordStems(cleaned);
    const words = cleaned.split(/\s+/);
    if (words.length > 1) {
        wordStems(words[0]!).forEach(s => stems.add(s));
        wordStems(words[words.length - 1]!).forEach(s => stems.add(s));
    }
    return stems;
}

const hasAny = (stems: ReadonlySet<string>, list: ReadonlySet<string>): boolean =>
    [...stems].some(s => list.has(s));

/** Whether `label` is an excluded (never-an-electrode) channel, e.g. `MKR1+` (#1088). */
export function isExcludedChannel(label: string, series?: ChannelSeries): boolean {
    // A series member is judged on its base: `SPO2` next to `SPO1` is not excluded, `DC02` is.
    const base = seriesBase(label, series);
    if (base !== null) return EXCLUDED_STEMS.has(base);
    return hasAny(nameStems(label), EXCLUDED_STEMS);
}

/**
 * Base name of a signed non-electrode channel (`EKG+` / `EKG-` → `EKG`), else `null`.
 * The base must be a known non-sEEG name ({@link NON_SEEG_STEMS}) and not excluded (#1088).
 */
export function signedPairBase(label: string): string | null {
    const sign = label?.slice(-1);
    if (sign !== '+' && sign !== '-') return null;
    const base = label.slice(0, -1);
    // One sign, at the end: "EKG+ - EKG-" is a derivation, not a signed channel.
    if (/[+-]/.test(base)) return null;
    const stems = nameStems(base);
    if (hasAny(stems, EXCLUDED_STEMS) || !hasAny(stems, NON_SEEG_STEMS)) return null;
    return base;
}

/** Whether the label heuristic, ignoring a leading `POL `, calls `label` non-sEEG (#1088). */
export function isNonElectrodeLabel(label: string, series?: ChannelSeries): boolean {
    return heuristicKind(label.replace(POL_PREFIX_RE, ''), series) === 'other';
}

/**
 * Whether `label` has a digit and a non-sEEG stem (`EMG1`, `POL EOG2`), ignoring a leading
 * `POL ` (#1088 code review). Unlike {@link isNonElectrodeLabel}, a digit-less label
 * (`EEG Fz`) is not reported, so its header verdict (#764) keeps deciding.
 */
export function hasNonSeegStem(label: string, series?: ChannelSeries): boolean {
    return /\d/.test(label ?? '') && isNonElectrodeLabel(label, series);
}
